import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { useRef, useEffect, useMemo, useState } from "react"
import { motion } from "motion/react"
import { useRequest } from "../../../hooks/useRequest"
import ConfirmModal, { ConfirmModalHandle } from "../../../components/ConfirmModal"
import SupportChatPanel from "../../../features/support/SupportChatPanel"
import { openSupportChat, refreshSupportUser } from "../../../lib/chatwoot"
import { User } from "../../../types/entities/user"
import { appStore } from "../../../store/appStore"
import { userStore } from "../../../store/userStore"
import NotificationBadge from "../../../components/NotificationBadge"
import { About, Bell, Exit, Favourite, History, Messages, Pencil, Promocode, RightArrow } from "../../../components/svgCollection"
import { openAuth } from "../../../lib/openAuth"
import { enablePushNotifications } from "../../../lib/pushOptIn"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type NavItemDef = {
    label: string
    to: string
    userOnly: boolean
    icon: React.ReactNode
    onClick?: (event: React.MouseEvent) => void
}

function ProfileDesktopContent() {
    const location = useLocation()
    const isSupport = location.pathname === "/profile/support"

    if (isSupport) {
        return <SupportChatPanel />
    }

    return <Outlet />
}

function ProfileSidebar() {
    const location = useLocation()
    const navigate = useNavigate()
    const pathName = location.pathname

    const confirmRef = useRef<ConfirmModalHandle>(null)
    const { user, setUser, clearAuth, deviceToken, unreadMessages } = userStore()
    const { city, cinema, setError } = appStore()
    const { request, requestData } = useRequest()
    const [pushLoading, setPushLoading] = useState(false)
    const [supportOpening, setSupportOpening] = useState(false)

    const pushState = useMemo(() => {
        if (typeof window === "undefined") return { supported: false, permission: "default" as NotificationPermission }
        if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
            return { supported: false, permission: "default" as NotificationPermission }
        }
        return { supported: true, permission: Notification.permission }
    }, [])

    useEffect(() => {
        if (!user) return
        request("GET", `user?city_id=${city.id}`)
            .then(data => setUser(data))
            .catch(() => {})
    }, [city.id, user?.id])

    const initials = user
        ? [user.first_name, user.last_name]
              .filter(Boolean)
              .map((w) => w[0])
              .join("")
              .toUpperCase()
        : null

    const card = user ? (user.cards.find((c) => !c.blocked) ?? null) : null

    const handleSupportClick = (event: React.MouseEvent) => {
        if (window.matchMedia("(min-width: 768px)").matches) return
        event.preventDefault()
        if (supportOpening) return

        const open = async () => {
            setSupportOpening(true)
            const currentUser = await refreshSupportUser(user, () =>
                request<User>("GET", `user?city_id=${city.id}`, undefined, { disableError: true })
            )
            if (currentUser) setUser(currentUser)

            await openSupportChat({
                user: currentUser,
                deviceToken,
                cityId: city.id,
                cityName: city.name,
                cinemaName: cinema.name,
            })
        }

        open()
            .catch(() => setError("Не удалось открыть чат поддержки. Попробуйте ещё раз."))
            .finally(() => setSupportOpening(false))
    }

    const onExitClick = () => {
        confirmRef.current?.open({
            title: "Уверены, что хотите выйти из профиля?",
            actions: [
                {
                    label: "Выйти",
                    variant: "danger",
                    onClick: async () => {
                        // Как в `kino`: logout удаляет связь пользователя с токеном (X-Firebase-ID-Token).
                        try {
                            await request("POST", "logout", undefined, { disableError: true })
                        } catch {
                            // даже если запрос не прошёл, локально всё равно выходим
                        } finally {
                            clearAuth()
                        }
                    },
                },
            ],
        })
    }

    const navItems: NavItemDef[] = [
        { icon: <Bell />, label: "Уведомления", to: "/profile/notifications", userOnly: true },
        { icon: <Favourite />, label: "Избранное", to: "/profile/favourites", userOnly: true },
        { icon: <History />, label: "История покупок", to: "/profile/history", userOnly: true },
        { icon: <Promocode />, label: "Промокод", to: "/profile/promocode", userOnly: true },
        { icon: <Messages />, label: "Поддержка", to: "/profile/support", userOnly: false, onClick: handleSupportClick },
        { icon: <About />, label: "О приложении", to: "/profile/about", userOnly: false },
    ]

    const filteredItems = user ? navItems : navItems.filter((i) => !i.userOnly)
    const fullName = user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : null

    return (
        <div className="flex flex-col gap-4 py-4">
            <ConfirmModal ref={confirmRef} />

            {/* User card */}
            <div className="rounded-[22px] border border-white/10 bg-white/[0.05] p-5 flex flex-col gap-3">
                {user ? (
                    <>
                        {/* Avatar + name */}
                        <div className="flex items-center gap-3">
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                className="size-16 rounded-full flex items-center justify-center text-white text-[20px] shrink-0"
                                style={{
                                    background: "linear-gradient(135deg, #e4206b 0%, #349be6 100%)",
                                    fontFamily: MONO,
                                    fontWeight: 800,
                                }}
                            >
                                {initials || "👤"}
                            </motion.div>
                            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                <p
                                    className="text-white truncate"
                                    style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 16, letterSpacing: "-0.01em" }}
                                >
                                    {fullName || "Пользователь"}
                                </p>
                                <p className="text-white/40 text-[13px] truncate" style={{ fontFamily: BODY }}>
                                    {user.phone}
                                </p>
                            </div>
                            <NavLink
                                to="/profile/edit"
                                aria-label="Редактировать профиль"
                                className={`flex size-9 shrink-0 items-center justify-center rounded-full border border-white/15 transition-colors ${pathName === "/profile/edit" ? "bg-[#349be6]/25" : "bg-white/[0.07] hover:bg-white/[0.14]"}`}
                            >
                                <Pencil />
                            </NavLink>
                        </div>

                        {/* Bonus badge */}
                        {card && (
                            <div className="flex items-center gap-2 rounded-[14px] border border-[#29cc6a]/30 bg-[#29cc6a]/10 px-4 py-2.5 self-start">
                                <span>💎</span>
                                <span
                                    className="text-[#29cc6a] text-[13px]"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}
                                >
                                    {card.movie.balance} бонусов
                                </span>
                            </div>
                        )}

                        {pushState.supported && pushState.permission !== "granted" && (
                            <motion.button
                                type="button"
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                                disabled={pushLoading}
                                onClick={async () => {
                                    setPushLoading(true)
                                    try {
                                        await enablePushNotifications(requestData)
                                    } finally {
                                        setPushLoading(false)
                                    }
                                }}
                                className={`mt-1 w-full rounded-[14px] px-4 py-3 text-left text-[13px] transition ${pushLoading ? "bg-white/[0.04] text-white/40" : "bg-[#349be6]/12 text-white/80 hover:bg-[#349be6]/18"}`}
                                style={{ fontFamily: MONO, fontWeight: 700 }}
                            >
                                {pushState.permission === "denied"
                                    ? "Уведомления запрещены в браузере"
                                    : pushLoading
                                      ? "Подключаем уведомления…"
                                      : "Включить push-уведомления"}
                            </motion.button>
                        )}
                    </>
                ) : (
                    <>
                        <div className="size-16 rounded-full flex items-center justify-center text-[28px] border border-white/10 bg-white/[0.05] shrink-0">
                            👤
                        </div>
                        <p className="text-white/50 text-[14px]" style={{ fontFamily: BODY }}>
                            Войди, чтобы увидеть профиль
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => openAuth(navigate, location)}
                            className="rounded-full py-3 px-5 text-white text-[13px] self-start"
                            style={{
                                background: "#e4206b",
                                fontFamily: MONO,
                                fontWeight: 700,
                                boxShadow: "0 0 18px rgba(228,32,107,0.4)",
                            }}
                        >
                            Войти по номеру
                        </motion.button>
                    </>
                )}
            </div>

            {/* Nav items */}
            <div className="flex flex-col gap-1 rounded-[22px] border border-white/10 bg-white/[0.05] p-3">
                {filteredItems.map((item, i) => (
                    <motion.div
                        key={item.to}
                        whileHover={{ x: 4 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    >
                        <NavLink
                            to={item.to}
                            onClick={item.onClick}
                            className={({ isActive }) =>
                                [
                                    "flex items-center gap-3 px-4 py-3 rounded-[14px] transition-all",
                                    item.to === "/profile/support" && supportOpening ? "pointer-events-none opacity-60" : "",
                                    isActive
                                        ? "bg-[#349be6]/15 text-white"
                                        : "text-white/60 hover:bg-white/[0.05] hover:text-white",
                                ].join(" ")
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <motion.span
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.04 }}
                                        className={`shrink-0 transition-opacity ${isActive ? "opacity-100" : "opacity-50"}`}
                                    >
                                        {item.icon}
                                    </motion.span>
                                    <span
                                        className="flex-1 text-[14px]"
                                        style={{ fontFamily: BODY, fontWeight: 600 }}
                                    >
                                        {item.to === "/profile/support" && supportOpening ? "Открываем чат..." : item.label}
                                    </span>
                                    {item.to === "/profile/notifications" && unreadMessages > 0 && (
                                        <span className="relative mr-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#e4206b] px-1 text-[10px] leading-none text-white"
                                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                                            {unreadMessages > 99 ? "99+" : unreadMessages}
                                        </span>
                                    )}
                                    <span className={`transition-opacity ${isActive ? "opacity-40" : "opacity-20"}`}>
                                        <RightArrow />
                                    </span>
                                </>
                            )}
                        </NavLink>
                    </motion.div>
                ))}

                {/* Logout */}
                {user && (
                    <motion.button
                        whileHover={{ x: 4 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        onClick={onExitClick}
                        className="flex items-center gap-3 px-4 py-3 rounded-[14px] hover:bg-[#e4206b]/10 transition-all mt-1 w-full"
                    >
                        <span className="shrink-0">
                            <Exit />
                        </span>
                        <span
                            className="flex-1 text-[14px] text-left text-[#e4206b]"
                            style={{ fontFamily: BODY, fontWeight: 600 }}
                        >
                            Выйти из профиля
                        </span>
                    </motion.button>
                )}
            </div>
        </div>
    )
}

export default function Profile() {
    const location = useLocation()
    const isRoot = location.pathname === "/profile"

    return (
        <div className="px-4">
            {/* Mobile: sidebar on root, outlet on sub-routes */}
            <div className="md:hidden">
                {isRoot ? <ProfileSidebar /> : <Outlet />}
            </div>

            {/* Desktop: sidebar + content side by side */}
            <div className="hidden gap-10 md:flex">
                <div className="sticky top-14 w-[400px] shrink-0 self-start max-h-[calc(100dvh-3.5rem)] overflow-y-auto">
                    <ProfileSidebar />
                </div>
                <div className="min-w-0 flex-1">
                    <ProfileDesktopContent />
                </div>
            </div>
        </div>
    )
}
